import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { colleagueInvitesApi, type ColleagueInviteInput } from "@/lib/api/colleague-invites";
import {
  COLLEAGUE_INVITES_KEY,
  INVITES_CHANGED_EVENT,
  inviteError,
  inviteSubmittedMessage,
} from "@/lib/colleague-invites";
import { RECONNECT_EVENT, usePortalEvent } from "@/lib/portal-socket";
import { ColleagueInvitesCard } from "./colleague-invites-card";

/** #437: company admins invite colleagues (the Colleagues page and the Company
 *  setup guide, #436 — `onInvited` re-checks the guide step). */
export function InvitesSection({ onInvited }: { onInvited?: () => void } = {}) {
  const qc = useQueryClient();
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: COLLEAGUE_INVITES_KEY });
    qc.invalidateQueries({ queryKey: ["accounts"] });
  };
  usePortalEvent(INVITES_CHANGED_EVENT, refresh);
  usePortalEvent(RECONNECT_EVENT, refresh);

  const { data } = useQuery({
    queryKey: COLLEAGUE_INVITES_KEY,
    queryFn: () => colleagueInvitesApi.list().then((r) => r.data),
    refetchInterval: 120_000,
  });

  const create = useMutation({
    mutationFn: (input: ColleagueInviteInput) => colleagueInvitesApi.create(input).then((r) => r.data),
    onSuccess: (_res, input) => {
      toast.success(inviteSubmittedMessage(input));
      onInvited?.();
    },
    onSettled: refresh,
  });

  const cancel = useMutation({
    mutationFn: (id: number) => colleagueInvitesApi.cancel(id),
    onMutate: (id) => setCancellingId(id),
    onSuccess: () => toast.success("Invite cancelled"),
    onError: (err) => toast.error(inviteError(err)),
    onSettled: () => {
      setCancellingId(null);
      refresh();
    },
  });

  return (
    <ColleagueInvitesCard
      invites={data ?? []}
      submitting={create.isPending}
      cancellingId={cancellingId}
      onInvite={(input) =>
        create.mutateAsync(input).then(
          () => true,
          (err) => {
            toast.error(inviteError(err));
            return false;
          },
        )
      }
      onCancel={(invite) => cancel.mutate(invite.id)}
    />
  );
}

export default InvitesSection;
